import type { ReactNode } from "react";

import { Button } from "@/components/ui/Button";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { hubModalBodyScrollClassName, hubModalShellClassName, type HubModalShellSize } from "@/lib/hubLayoutClassNames";
import { cn } from "@/lib/utils";

export function DocumentModal({
  title,
  titleTrailing,
  headerExtra,
  description,
  children,
  onClose,
  closeOnOverlayClick = false,
  showCloseButton = true,
  size = "document",
  shellClassName,
  overlayClassName,
  /** Bottom pane padding + layout (defaults to scrolling padded body). */
  mainClassName,
}: {
  title: string;
  titleTrailing?: ReactNode;
  headerExtra?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  size?: HubModalShellSize;
  shellClassName?: string;
  overlayClassName?: string;
  mainClassName?: string;
}) {
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-8",
        overlayClassName,
      )}
      onMouseDown={(event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={
          shellClassName
            ? cn(
                "my-4 flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl",
                shellClassName,
              )
            : hubModalShellClassName(size)
        }
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
              <h2 className="text-lg font-semibold leading-snug text-slate-900">{title}</h2>
              {titleTrailing ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2">{titleTrailing}</div>
              ) : null}
            </div>
            {headerExtra ? (
              typeof headerExtra === "string" ? (
                <p className="mt-2 text-sm text-slate-600">{headerExtra}</p>
              ) : (
                <div className="mt-2">{headerExtra}</div>
              )
            ) : null}
            {description ? (
              typeof description === "string" ? (
                <p className="mt-2 text-sm text-slate-600">{description}</p>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">{description}</div>
              )
            ) : null}
          </div>
          {showCloseButton ? (
            <HoverHintTrigger
              trigger={
                <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                  Close
                </Button>
              }
            >
              Close this dialog. Unsaved changes in other fields may still need Save on the underlying page.
            </HoverHintTrigger>
          ) : null}
        </div>
        <div className={cn(hubModalBodyScrollClassName(), "px-6 py-5", mainClassName)}>{children}</div>
      </div>
    </div>
  );
}
